import React, { useState, useEffect } from "react";
import { FaRegHeart } from "react-icons/fa";
import { FaHeart } from "react-icons/fa6";
import { FiMessageSquare } from "react-icons/fi";
import PhotoCarousel from "./PhotoCarousel";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createDirectChat,
  getUserByEmail,
  toggleLike,
  getLikesSnap,
} from "../../../utils/firebase/Firebase.utils";
import PropertyDetailsModal from "./PropertyDetailsModal";

export default function PropertyCard({ data }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const currentUser = useSelector((state) => state.userObject);
  const navigate = useNavigate();
  const [sellerId, setSellerId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Fetch seller's user ID if we have their email
    const fetchSellerId = async () => {
      if (data.createdBy && !data.createdBy.includes("_")) {
        // If it's an email (doesn't contain underscore like UIDs do)
        try {
          const sellerData = await getUserByEmail("users", data.createdBy);
          if (sellerData) {
            setSellerId(sellerData.id);
          }
        } catch (error) {
          console.error("Error fetching seller ID:", error);
        }
      } else {
        setSellerId(data.createdBy); // If it's already a UID
      }
    };

    fetchSellerId();
  }, [data.createdBy]);

  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to likes updates
    const unsubscribe = getLikesSnap(data.id, "property", (likesData) => {
      setLiked(likesData.likes.includes(currentUser.uid));
      setLikeCount(likesData.count || 0);
    });

    return () => unsubscribe();
  }, [data.id, currentUser]);

  const handleContactSeller = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (!sellerId) {
      console.error("Seller ID not found");
      return;
    }

    try {
      // Create or get existing chat
      const chatId = await createDirectChat(currentUser.uid, sellerId, {
        propertyId: data.id,
        propertyTitle: data.unitNumber,
        propertyImage: data.images?.[0] || null,
        monthlyRent: data.monthlyRent,
        type: data.type,
      });

      // Navigate to chat
      navigate(`/chats/${chatId}`);
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  const handleToggleLike = async (e) => {
    e.stopPropagation();

    if (!currentUser) {
      navigate("/login");
      return;
    }

    try {
      const isLiked = await toggleLike(data.id, currentUser.uid, "property");
      setLiked(isLiked);
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  return (
    <>
      <div
        className="relative p-2 bg-white rounded-lg shadow-md mt-2 hover:shadow-lg transition-all duration-300 w-[350px] self-center cursor-pointer"
        onClick={() => setShowModal(true)}
      >
        <div className="overflow-hidden rounded-lg">
          <PhotoCarousel images={data.images} />
        </div>
        <div className="bg-[#8FCD00] text-sm font-bold rounded-lg w-fit px-2 py-1 absolute top-4 right-4">
          {data.type}
        </div>
        <div className="p-3">
          <div className="font-bold text-[#363062]">₱{data.monthlyRent}</div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#363062] text-sm">{data.unitNumber}</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{likeCount}</span>
              <button onClick={handleToggleLike}>
                <div className="text-2xl">
                  {liked ? (
                    <FaHeart className="text-red-500" />
                  ) : (
                    <FaRegHeart />
                  )}
                </div>
              </button>
            </div>
          </div>

          <button
            onClick={handleContactSeller}
            disabled={!sellerId}
            className="w-full bg-[#363062] hover:bg-[#363062]/90 text-white py-2 px-4 rounded-md transition-colors duration-300 text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiMessageSquare className="w-4 h-4" />
            Contact Owner
          </button>

          <div
            className="underline cursor-pointer text-xs text-gray-400 mt-2 text-center"
            onClick={(e) => {
              e.stopPropagation();
              setShowModal(true);
            }}
          >
            View Details
          </div>
        </div>
      </div>

      {showModal && (
        <PropertyDetailsModal
          property={data}
          onClose={() => setShowModal(false)}
          onContactSeller={handleContactSeller}
          liked={liked}
          onToggleLike={handleToggleLike}
          likeCount={likeCount}
        />
      )}
    </>
  );
}